/**
 * Carousel Service - Frontend API Client
 * Version 3.9.0: Home page carousel management
 */

import api from './api.js';

/**
 * Get active carousel images (public)
 */
export async function getActiveImages() {
  const response = await api.get('/carousel');
  return response.data;
}

/**
 * Get public carousel display settings (auto-play interval).
 */
export async function getSettings() {
  const response = await api.get('/carousel/settings');
  return response.data;
}

/**
 * Update carousel display settings (admin only).
 * @param {{ autoplay_interval_ms: number }} settings
 */
export async function updateSettings(settings) {
  const response = await api.put('/carousel/admin/settings', settings);
  return response.data;
}

/**
 * Get all carousel images (admin only)
 */
export async function getAllImages() {
  const response = await api.get('/carousel/admin');
  return response.data;
}

/**
 * Get single carousel image (admin only)
 */
export async function getImageById(id) {
  const response = await api.get(`/carousel/admin/${id}`);
  return response.data;
}

/**
 * Create new carousel image (admin only)
 */
export async function createImage(imageData) {
  const response = await api.post('/carousel/admin', imageData);
  return response.data;
}

/**
 * Update carousel image (admin only)
 */
export async function updateImage(id, updates) {
  const response = await api.put(`/carousel/admin/${id}`, updates);
  return response.data;
}

/**
 * Delete carousel image (admin only)
 */
export async function deleteImage(id) {
  const response = await api.delete(`/carousel/admin/${id}`);
  return response.data;
}

/**
 * Reorder carousel images (admin only)
 */
export async function reorderImages(imageOrders) {
  const response = await api.post('/carousel/admin/reorder', { imageOrders });
  return response.data;
}

/**
 * Upload and process image (admin only). Generates two variants server-side:
 *   - desktop: 1920x600 (16:5) wide banner
 *   - mobile:  1080x1350 (4:5) portrait
 * @param {File} file - Image file to upload
 * @param {Object} cropOptions - Pixel crops from react-easy-crop editors
 * @param {function} [onProgress] - Progress callback
 */
export async function uploadImage(file, cropOptions = {}, onProgress) {
  const {
    desktopCrop,
    desktopZoom = 100,
    mobileCrop,
    mobileZoom = 100,
    desktopPosition = 50,
    mobileFocusX = 50,
    mobileFocusY = 50,
  } = cropOptions;

  const formData = new FormData();
  formData.append('image', file);

  if (desktopCrop) {
    formData.append('desktopCrop', JSON.stringify(desktopCrop));
    formData.append('desktopZoom', String(desktopZoom));
  } else {
    formData.append('cropPosition', desktopPosition.toString());
  }

  if (mobileCrop) {
    formData.append('mobileCrop', JSON.stringify(mobileCrop));
    formData.append('mobileZoom', String(mobileZoom));
  } else {
    formData.append('mobileFocusX', String(mobileFocusX));
    formData.append('mobileFocusY', String(mobileFocusY));
  }

  const response = await api.post('/carousel/admin/upload', formData, {
    onUploadProgress: (progressEvent) => {
      if (onProgress && progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(percentCompleted);
      }
    }
  });

  return response.data;
}

/**
 * Get carousel doctor report — for each row, is the file actually present
 * on disk / in S3? Plus list of orphan files (on disk, no DB row).
 *
 * Returns: { success, data: { summary, rows, orphans } }
 */
export async function getDoctorReport() {
  const response = await api.get('/carousel/admin/doctor');
  return response.data;
}

/**
 * Delete files on disk that have no matching DB row.
 * Safe: only deletes files inside the configured carousel uploads dir.
 */
export async function cleanupOrphans() {
  const response = await api.post('/carousel/admin/cleanup-orphans');
  return response.data;
}
